/**
 * SamsaraS3Video is a dynamic form field component that displays a video 
 * pulled from the Samsara API using an identifier in the form field.
 * 
 * If the video is not found, or loading, appropriate placeholders or spinners are shown.
 * A config field allows editing the identifier (e.g., incident ID and direction).
 * 
 * Important:
 * - A `forward` or `inward` direction is inferred from a suffix in the identifier (e.g., `12345-f`).
 * - The video is fetched as a blob and converted to a local blob URL using URL.createObjectURL.
 * - A debounce is used to prevent excessive fetches when the identifier is updated rapidly.
 */

// React imports
import { FunctionComponent, useEffect, useRef, useState } from "react"

// Third-party libraries
import axios from "axios"
import { FormControl, OverlayTrigger } from "react-bootstrap"

// Types
import FieldTypes from "src/pages/dynamic_forms/Types/FieldTypes"
import DynamicFormPaletteTypes from "src/store/DyanmicFormPalette/types"

// Utilities and helpers
import TrashCan from "src/pages/dynamic_forms/Utils/TrashCan/TrashCan"
import paletteEngine from "src/pages/dynamic_forms/Utils/paletteEngine"
import mobileFriendlyWarning from "src/pages/dynamic_forms/Utils/MobileFriendlyWarning/MobileFriendlyWarning"
import reoccuringText from "src/pages/dynamic_forms/Utils/ReoccuringText"
import AxiosConfig from "src/utilities/AxiosConfig"

// Components
import VideoPlayer from "src/components/video_player/VideoPlayer"
import LDSEllipsis from "src/components/common/LDS/LDSEllipsis/LDSEllipsis"

// Styles
import "./SamsaraS3Video.css"
import "../../DynamicForm.css"

interface Props {
    manageParentState: Function
    handleChange: Function
    field: FieldTypes
    row: number
    col: number
    formManagerField?: boolean
    configField?: boolean
    templateField?: boolean
    remove: Function
    palette: Array<DynamicFormPaletteTypes>
    readOnly?: boolean
}

const SamsaraS3Video: FunctionComponent<Readonly<Props>> = (props) => {

    const {
        manageParentState,
        handleChange,
        field,
        row,
        col,
        formManagerField,
        configField,
        templateField,
        remove
    } = props

    // Track if a fetch is already in progress
    const busyRef = useRef(false)

    // Used to debounce video fetches
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const name = field.Name ?? "S3 Video"
    const identifier = field.CustomValues.length > 0 ? field.CustomValues[0] : null
    const [videoUrl, setVideoUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    // Set field type (used for CSS class naming)
    let fieldType = "Field"
    if (templateField) fieldType = "TemplateField"
    else if (formManagerField) fieldType = "FormMangerField"
    else if (configField) fieldType = "ConfigField"

    useEffect(() => {
        // If identifier is invalid or this is a config field, skip video fetch
        if (!identifier || configField) {
            setVideoUrl(null)
            return
        }

        if (busyRef.current) {
            console.debug("Fetch skipped because busyRef is true")
            return
        }

        const [idPart, dirPart] = identifier.split("-")
        const objectId = parseInt(idPart)
        const direction = dirPart === "i" ? "inward" : "forward"

        if (isNaN(objectId)) {
            console.warn("Invalid objectId in identifier:", identifier)
            setVideoUrl(null)
            return
        }

        let isCancelled = false
        let currentBlobUrl: string | null = null

        // Clear any existing debounce
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
        }

        // Debounce the fetchVideo logic to prevent rapid requests
        debounceTimeoutRef.current = setTimeout(() => {
            const fetchVideo = async () => {
                busyRef.current = true
                setLoading(true)
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL}/api/samsara/video-stream`,
                        {
                            ...AxiosConfig.configValue(),
                            params: {
                                objectId,
                                direction
                            },
                            responseType: "blob"
                        }
                    )

                    if (isCancelled) return

                    currentBlobUrl = URL.createObjectURL(response.data)
                    setVideoUrl(currentBlobUrl)
                } catch (error) {
                    console.error("Failed to fetch video blob", error)
                    setVideoUrl(null)
                } finally {
                    setLoading(false)
                    busyRef.current = false
                }
            }

            fetchVideo()
        }, 1000)

        // Cleanup: cancel fetch and clear URL blob if component unmounts
        return () => {
            isCancelled = true
            if (currentBlobUrl) {
                URL.revokeObjectURL(currentBlobUrl)
            }
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }
        }
    }, [identifier, configField])

    return (
        <div
            className={`DynamicForm${fieldType} DynamicS3Video${fieldType}`}
            style={{ backgroundColor: paletteEngine.getPaletteColor("fieldBackgroundColor") }}
        >
            {/* Header: shows field label and optional config/trash icons */}
            <div className="DynamicFormFieldHeader">
                {!formManagerField && (
                    <div className="DynamicFormFieldHeaderLeft">
                        <p style={{ color: paletteEngine.getPaletteColor("headerTextColor") }}>{name}</p>
                    </div>
                )}
                <div className="DynamicFormFieldHeaderRight">
                    {templateField && (
                        <div className="DynamicFormFieldHeaderRightSettings">
                            <div className="DynamicFormFieldHeaderRightSettingsMultiColumnarWarning">
                                {col > 0 && (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={mobileFriendlyWarning("Rows with multiple columns", reoccuringText.multipleColumnInRowWarningText)}
                                    >
                                        <span
                                            className="material-icons"
                                            style={{ color: paletteEngine.getPaletteColor("editorConfigMultiColumnsWarningIconColor") }}
                                        >
                                            priority_high
                                        </span>
                                    </OverlayTrigger>
                                )}
                            </div>
                            <span
                                className="material-icons DynamicFormFieldHeaderRightSettingsGear"
                                style={{ color: paletteEngine.getPaletteColor("editorConfigGearColor") }}
                                onClick={() =>
                                    manageParentState([{ key: "configTarget", value: { row, col } }])
                                }
                            >
                                settings
                            </span>
                            <TrashCan
                                clickCallback={() => remove(field.Type, row, col, true)}
                                color={paletteEngine.getPaletteColor("editorTrashBinColor")}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Body: shows either input, video, or fallback state */}
            <div className="DynamicFormFieldBody">
                {configField && (
                    <FormControl
                        onFocus={() =>
                            manageParentState([{ key: "target", value: { row, col, key: "CustomValues" } }])
                        }
                        onChange={(e) => handleChange({ target: { value: [e.target.value] } })}
                        placeholder="IncidentID-f/i"
                        value={identifier ?? ""}
                        style={{
                            backgroundColor: paletteEngine.getPaletteColor("fieldInputBackgroundColor"),
                            color: paletteEngine.getPaletteColor("fieldInputTextColor")
                        }}
                    />
                )}

                {!loading && <>
                    {videoUrl && (
                        <div className="DynamicFormFieldBodyS3VideoItem">
                            <VideoPlayer
                                src={videoUrl}
                                autoPlay
                                width="100%"
                                height="auto"
                                controls
                                captionsSrc=""
                                captionsLabel="English"
                                captionsLang="en"
                            />
                        </div>
                    )}
                    {(!videoUrl && !configField) && (
                        <div className="DynamicFormFieldBodyS3VideoItemNotFound">
                            <p>Video Not found</p>
                            <span className="material-icons">videocam_off</span>
                        </div>
                    )}
                </>}
                {loading &&
                    <div className="DynamicFormFieldBodyS3VideoItemLoading">
                        <p>Loading Video...</p>
                        <LDSEllipsis />
                    </div>}
            </div>
        </div>
    )
}

export default SamsaraS3Video
